import React, { memo } from 'react';
import { CumulativePointsProgramParticipantStatus } from 'src/models/rewards/Rewards';
import { cumulativePointsCallToActionMessage, isParticipantProgramEnded } from 'src/utils/utils';
import { AvailabilityState } from 'src/utils/enums';
import CumulativeProgramPoint from 'src/components/molecules/cumulative-program-point/CumulativeProgramPoint';
import RewardProgramCard from 'src/components/molecules/reward-program-card/RewardProgramCard';
import RewardProgressBar from 'src/components/molecules/reward-progress-bar/RewardProgressBar';
import { useStyles } from './CumulativePointsProgramCard.styles';

interface Props {
  participantStatus: CumulativePointsProgramParticipantStatus
}

const CumulativePointsProgramCard: React.FC<Props> = ({ participantStatus }) => {
  const styles = useStyles();
  const { name, programTypeStatus } = participantStatus ?? {};
  const {
    rewards,
    earnedNextPoint,
    rewardsNames,
    pointsNames,
    state,
    totalPointsEarned = 0,
  } = programTypeStatus ?? {};

  const requiresPoints = rewards?.map(reward => reward.requiredPoints) || [];
  const targetPoints = requiresPoints[requiresPoints.length - 1] || 0;
  const isTargetReached = totalPointsEarned >= targetPoints && isParticipantProgramEnded(state);
  const currentTotalPoint = totalPointsEarned > targetPoints ? targetPoints : totalPointsEarned;
  const earnedNextPointMessage = state === AvailabilityState.Available
    ? `Earn your next ${rewardsNames.singular.toLowerCase()} in ${earnedNextPoint} ${pointsNames.plural.toLowerCase()}`
    : '';
  const ctaMessage = cumulativePointsCallToActionMessage(participantStatus);

  const renderProgressBar = (): JSX.Element | null => {
    if (requiresPoints.length === 0) return null;

    return (
      <div className={styles.cumulativePointsProgressBarWrapper}>
        <CumulativeProgramPoint
          totalPointsEarned={currentTotalPoint}
          pointsLabel={pointsNames.plural.toLowerCase()}
          earnNextPointMessage={earnedNextPointMessage}
        />

        <RewardProgressBar
          totalPointsEarned={totalPointsEarned}
          rewardsRequiredPoints={requiresPoints}
        />
      </div>
    );
  };

  return (
    <RewardProgramCard
      programName={name}
      programParticipantStatus={participantStatus}
      cta={ctaMessage}
      isProgramTargetReached={isTargetReached}
    >
      {state !== AvailabilityState.NotAvailableYet && renderProgressBar()}
    </RewardProgramCard>
  );
};

export default memo(CumulativePointsProgramCard);
